import { ChangeDetectionStrategy, Component, EventEmitter, inject, Output } from '@angular/core';
import { finalize } from 'rxjs';
import { WarehouseService } from '../../../services/warehouse.service';
import { ProductImportResult } from '../warehouse.types';
import { TableV2Column } from '../../table-v2/table-v2.interface';

interface ImportColumn {
  name: string;
  /** Translation key of what it holds */
  meaning: string;
  required?: boolean;
}

/** Imports products with variants from CSV / XLSX; a product with an invalid row is skipped as a whole */
@Component({
  selector: 'app-warehouse-product-import',
  templateUrl: './product-import.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class WarehouseProductImportComponent {
  /** Whether something was imported */
  @Output() close = new EventEmitter<boolean>();

  private readonly warehouseService = inject(WarehouseService);

  protected readonly columns: ImportColumn[] = [
    { name: 'name / назва', meaning: 'stock.import.column_name', required: true },
    { name: 'category / категорія', meaning: 'stock.import.column_category' },
    { name: 'unit / одиниця', meaning: 'stock.import.column_unit' },
    { name: 'currency / валюта', meaning: 'stock.import.column_currency' },
    { name: 'description / опис', meaning: 'stock.import.column_description' },
    { name: 'sku / артикул', meaning: 'stock.import.column_sku' },
    { name: 'barcode / штрихкод', meaning: 'stock.import.column_barcode' },
    { name: 'price / ціна', meaning: 'stock.import.column_price' },
    { name: 'property:Колір', meaning: 'stock.import.column_property' },
  ];
  protected readonly tableColumns: TableV2Column<ImportColumn>[] = [
    { key: 'name', header: 'stock.import.column', padding: 'px-3', nowrap: true, cellClass: 'font-mono text-xs' },
    { key: 'meaning', header: 'stock.import.meaning', padding: 'px-3', cellClass: 'text-xs text-slate-600' },
  ];

  protected file: File | null = null;
  protected fileError: string | null = null;
  protected importing = false;
  protected dragOver = false;
  protected result: ProductImportResult | null = null;
  private imported = false;

  protected setFile(file: File | null | undefined): void {
    this.result = null;
    this.file = file ?? null;
    this.fileError = !file
      ? null
      : !/\.(csv|xlsx)$/i.test(file.name)
        ? 'stock.errors.IMPORT_FILE_INVALID.csv_or_xlsx_required'
        : file.size > 10 * 1024 * 1024
          ? 'stock.import.file_too_big'
          : null;
  }

  protected startImport(): void {
    if (!this.file || this.importing || this.fileError) return;

    this.importing = true;
    this.result = null;
    this.warehouseService
      .importProducts(this.file)
      .pipe(finalize(() => (this.importing = false)))
      .subscribe({
        next: (result) => {
          this.result = result;
          this.file = null;
          this.imported ||= !!(result.products_created || result.variants_created);
        },
        error: (error) => this.warehouseService.error(error),
      });
  }

  /** Also called by the sidebar close button: the result has to reach the list, and an import can not be dropped */
  public requestClose(): void {
    if (!this.importing) this.close.emit(this.imported);
  }
}
